import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SiteNav } from "@/components/SiteChrome";
import { toast } from "sonner";
import { Plus, Upload, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EditionPill } from "@/components/PackCard";
import { PackFormDialog } from "@/components/PackFormDialog";
import { EntryImportDialog } from "@/components/EntryImportDialog";
import { PackRequestsAdmin } from "@/components/PackRequestsAdmin";
import { VerifyButton, VerifyResultDialog, VerifiedBadge } from "@/components/VerifyPack";
import { api, errMsg, fmtDate } from "@/lib/api";

const STATUSES = ["Draft", "Needs review", "Published", "Archived"];

const PackRow = ({ pack, onEdit, onImport, onChanged, onVerified }) => {
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-4 p-6 lg:grid-cols-12 lg:items-center" data-testid={`admin-pack-row-${pack.id}`}>
      <div className="lg:col-span-5">
        <div className="font-display text-lg font-bold leading-tight">{pack.title}</div>
        <div className="mt-1 text-sm text-slate-500">{pack.textbook_title} · {pack.edition} · {pack.subject}</div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <EditionPill label={pack.edition_label} />
          <VerifiedBadge verification={pack.verification} testId={`admin-pack-verified-${pack.id}`} />
          {pack.verification && !pack.verification.verified && <span data-testid={`admin-pack-unverified-${pack.id}`} className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-800">Differences found {fmtDate(pack.verification.checked_at)}</span>}
          {!pack.verification && <span data-testid={`admin-pack-notchecked-${pack.id}`} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold text-slate-600">Not yet verified</span>}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-sm lg:col-span-4">
        <div><div className="text-[11px] uppercase tracking-wider text-slate-500">Entries</div><div className="font-semibold" data-testid={`admin-pack-entry-count-${pack.id}`}>{pack.entry_count}</div></div>
        <div><div className="text-[11px] uppercase tracking-wider text-slate-500">Version</div><div className="font-semibold">v{pack.version}</div></div>
        <div><div className="text-[11px] uppercase tracking-wider text-slate-500">Checked</div><div className="font-semibold" data-testid={`admin-pack-checked-${pack.id}`}>{fmtDate(pack.date_last_checked)}</div></div>
      </div>
      <div className="flex flex-wrap items-center gap-2 lg:col-span-3 lg:justify-end">
        <Select value={pack.status} onValueChange={(status) => act(() => api.post(`/admin/packs/${pack.id}/status`, { status }), `Status set to ${status}`)}>
          <SelectTrigger className="h-9 w-36 bg-white" data-testid={`admin-pack-status-select-${pack.id}`}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`admin-status-option-${s.toLowerCase().replace(" ", "-")}`}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="icon" variant="ghost" title="Edit details" onClick={() => onEdit(pack)} data-testid={`admin-pack-edit-${pack.id}`}><Pencil className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" title="Import entries" onClick={() => onImport(pack)} data-testid={`admin-pack-import-${pack.id}`}><Upload className="h-4 w-4" /></Button>
        <VerifyButton pack={pack} onResult={(r) => { onVerified(r); onChanged(); }} />
        {["Draft", "Archived"].includes(pack.status) && (
          <Button size="icon" variant="ghost" title="Delete pack" data-testid={`admin-pack-delete-${pack.id}`}
            onClick={() => window.confirm(`Delete “${pack.title}” and all its entries?`) && act(() => api.delete(`/admin/packs/${pack.id}`), "Pack deleted")}>
            <Trash2 className="h-4 w-4 text-red-600" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default function AdminPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const [importing, setImporting] = useState(null);
  const [verifyResult, setVerifyResult] = useState(null);
  const load = useCallback(() => api.get("/admin/packs").then((r) => setPacks(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const [fromRequest, setFromRequest] = useState(null);
  const [reqKey, setReqKey] = useState(0);
  const createFromRequest = (r) => { setFromRequest(r); setEditing(null); };
  const closeForm = () => { setEditing(undefined); setFromRequest(null); };
  const onSaved = async () => {
    load();
    if (fromRequest) {
      await api.put(`/admin/pack-requests/${fromRequest.id}`, { status: "Planned", admin_note: fromRequest.admin_note || "We've started this pack." });
      setReqKey(reqKey + 1);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
    <SiteNav right={<button data-testid="logout-button" onClick={async () => { await logout(); navigate("/packs"); }} className="rounded-lg border border-slate-300 bg-white px-4 py-2 font-semibold text-slate-700 hover:bg-slate-100">Sign out</button>} />
    <div className="mx-auto max-w-6xl space-y-10 px-6 py-12">
      <header className="gl-fade-up flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pack Studio · Admin</p>
          <h1 className="font-display mt-1 text-4xl font-extrabold tracking-tight sm:text-5xl">Manage Gloss Packs</h1>
          <p className="mt-3 max-w-2xl text-slate-600">One pack per textbook edition. Import the official glossary word for word, verify every entry against the source page, then publish — it appears on glosslink.app/packs right away. Students just click "Add to my GlossLink".</p>
        </div>
        <Button className="rounded-lg bg-blue-600 hover:bg-blue-700" onClick={() => setEditing(null)} data-testid="admin-new-pack-button"><Plus className="mr-2 h-4 w-4" />New pack</Button>
      </header>
      <div className="divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="admin-pack-list">
        {packs.map((p) => <PackRow key={p.id} pack={p} onEdit={setEditing} onImport={setImporting} onChanged={load} onVerified={setVerifyResult} />)}
      </div>
      <PackFormDialog open={editing !== undefined} pack={editing} onOpenChange={(o) => !o && closeForm()} onSaved={onSaved}
        prefill={fromRequest && { textbook_title: fromRequest.textbook_title, edition: fromRequest.edition, subject: fromRequest.subject, source_url: fromRequest.glossary_url }} />
      <VerifyResultDialog result={verifyResult} onOpenChange={(o) => !o && setVerifyResult(null)} />
      <EntryImportDialog pack={importing} onOpenChange={(o) => !o && setImporting(null)} onDone={load} />
      <PackRequestsAdmin onCreatePack={createFromRequest} refreshKey={reqKey} />
    </div>
    </div>
  );
}
